import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from "framer-motion";
import { formatINR } from "@/lib/api";

export default function ProductCard({ product, index = 0 }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useTransform(my, [-0.5, 0.5], [4, -4]);
  const ry = useTransform(mx, [-0.5, 0.5], [-4, 4]);
  const rxSpring = useSpring(rx, { stiffness: 220, damping: 18 });
  const rySpring = useSpring(ry, { stiffness: 220, damping: 18 });

  function handleMove(e) {
    if (reduce) return;
    const rect = ref.current.getBoundingClientRect();
    const isTouch = window.matchMedia("(hover: none)").matches;
    if (isTouch) return;
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  }
  function handleLeave() { mx.set(0); my.set(0); }

  const discount = product.original_price && product.original_price > product.price
    ? Math.round(((product.original_price - product.price) / product.original_price) * 100)
    : null;

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      whileHover={reduce ? {} : { y: -8 }}
      whileTap={{ scale: 0.99 }}
      style={reduce ? {} : { rotateX: rxSpring, rotateY: rySpring, transformPerspective: 1000 }}
      transition={{ type: "spring", stiffness: 260, damping: 22 }}
      className="group relative"
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.id}`} className="block">
        <div className="relative overflow-hidden bg-[#163A2F]/[0.04] aspect-[4/5]">
          {product.is_bestseller && (
            <span className="absolute top-3 left-3 z-10 bg-[#C9A227] text-white text-[10px] uppercase tracking-[0.2em] px-2 py-1">Best Seller</span>
          )}
          {discount && (
            <span className="absolute top-3 right-3 z-10 bg-[#163A2F] text-white text-[10px] uppercase tracking-[0.2em] px-2 py-1">-{discount}%</span>
          )}
          <img
            src={product.images?.[0]}
            alt={product.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-[1.07]"
          />
          {product.images?.[1] && (
            <img
              src={product.images[1]}
              alt=""
              aria-hidden
              className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            />
          )}
        </div>
        <div className="pt-4 px-1">
          <div className="overline text-[#C9A227] mb-1.5">{product.category?.replace(/-/g, ' ')}</div>
          <div className="flex items-baseline justify-between gap-3">
            <h3 className="text-[#163A2F] text-[15px] font-medium leading-snug">{product.name}</h3>
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-[#163A2F] text-[15px] font-medium">{formatINR(product.price)}</span>
            {product.original_price && (
              <span className="text-[#163A2F]/40 text-sm line-through font-light">{formatINR(product.original_price)}</span>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
